import React, { useEffect, useState } from "react";
import areObjectsEqual from "../../../../utils/areObjectsEqual";
import Dropdown from "../../../Common/Dropdown";

const DEFAULT_TOP_FUNDRAISERS_TEXT =
  "These three NFTs have raised the most funds for this campaign. Each time someone buys one, more money is raised. Would you like to own one and help our beneficiary?";

/**
 * Form Component
 * Form for managing campaign website settings
 */
const Form = ({ formData, handleSubmit, beneficiary, sponsor }) => {
  const [form, setForm] = useState(formData || {});
  const [copyData, setCopyData] = useState();
  const [errors, setErrors] = useState({
    web_main_color: false,
    secondary_color: false,
    main_text_color: false,
    light_text_color: false,
    gradient_color_1: false,
    gradient_color_2: false,
    general_font: false,
    nft_name: false,
    nft_name_plural: false,
    purpose: false,
    hero_page_text: false,
  });
  const [topFundraisersLimit, setTopFundraisersLimit] = useState(false);
  const hasUnsavedChanges = areObjectsEqual(copyData, form);

  useEffect(() => {
    setForm(formData);
    setCopyData(formData);
  }, [formData]);

  /**
   * Handle input field changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (value.trim() === "" && value.length > 0) return;

    if (name === "ct_about_top_fundraisers") {
      setTopFundraisersLimit(value.length === 400);
    }

    setForm((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  /**
   * Handle dropdown changes
   */
  const handleDropdownChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Validate form
   */
  const validateForm = () => {
    const newErrors = {
      web_main_color: !form?.web_main_color,
      secondary_color: !form?.secondary_color,
      main_text_color: !form?.main_text_color,
      light_text_color: !form?.light_text_color,
      gradient_color_1: !form?.gradient_color_1,
      gradient_color_2: !form?.gradient_color_2,
      general_font: !form?.general_font,
      nft_name: !form?.nft_name,
      nft_name_plural: !form?.nft_name_plural,
      purpose: !form?.purpose,
      hero_page_text: !form?.hero_page_text,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error);
  };

  /**
   * Handle form submission
   */
  const handleClick = () => {
    if (validateForm()) {
      const submitData = {
        ...form,
        ct_check_website_updated: true,
        ct_about_top_fundraisers:
          form?.ct_about_top_fundraisers || DEFAULT_TOP_FUNDRAISERS_TEXT,
      };
      handleSubmit(submitData);
      setCopyData(submitData);
    }
  };

  return (
    <>
      <h4 className="p-3 form-title">Color Settings</h4>
      <div className="col-sm-12 text-label-left">
        <div className="row g-4 mb-4">
          <div className="col-sm-6">
            <label htmlFor="web_main_color" className="text-gray form-labels">
              Web Main Color
            </label>
            <div className="row">
              <div className="input-container">
                <input
                  type="color"
                  name="web_main_color"
                  id="web_main_color"
                  value={form?.web_main_color || "#000000"}
                  onChange={handleChange}
                  className="form-control mt-2 color-picker inputs"
                />
                {errors.web_main_color && (
                  <span className="form-text text-danger">
                    Web Main Color is required
                  </span>
                )}
              </div>
            </div>
            <div className="tiny-description-input">
              Select a dark color. This color is applied to menu bar, bottom
              bar, and some titles.
            </div>
          </div>

          <div className="col-sm-6">
            <label htmlFor="secondary_color" className="text-gray form-labels">
              Secondary Color
            </label>
            <div className="row">
              <div className="input-container">
                <input
                  type="color"
                  name="secondary_color"
                  id="secondary_color"
                  onChange={handleChange}
                  value={form?.secondary_color || "#000000"}
                  className="form-control mt-2 color-picker inputs"
                />
                {errors.secondary_color && (
                  <span className="form-text text-danger">
                    Secondary color is required
                  </span>
                )}
              </div>
            </div>
            <div className="tiny-description-input">
              This color is applied to the whole website, and some texts.
            </div>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-sm-6">
            <label htmlFor="main_text_color" className="text-gray form-labels">
              Main Text Color
            </label>
            <div className="row">
              <div className="input-container">
                <input
                  type="color"
                  name="main_text_color"
                  onChange={handleChange}
                  value={form?.main_text_color || "#000000"}
                  className="form-control mt-2 color-picker inputs"
                  id="main_text_color"
                />
                {errors.main_text_color && (
                  <span className="form-text text-danger">
                    Main Text Color is required
                  </span>
                )}
              </div>
            </div>
            <div className="tiny-description-input">
              This color is applied to the main text in the whole website.
            </div>
          </div>

          <div className="col-sm-6">
            <label htmlFor="light_text_color" className="text-gray form-labels">
              Light Text Color
            </label>
            <div className="row">
              <div className="input-container">
                <input
                  type="color"
                  name="light_text_color"
                  id="light_text_color"
                  onChange={handleChange}
                  value={form?.light_text_color || "#000000"}
                  className="form-control mt-2 color-picker inputs"
                />
                {errors.light_text_color && (
                  <span className="form-text text-danger">
                    Light text is required
                  </span>
                )}
              </div>
            </div>
            <div className="tiny-description-input">
              This is the color applied when you select and object.
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-sm-6">
            <label htmlFor="gradient_color_1" className="text-gray form-labels">
              Gradient Color 1
            </label>
            <div className="row">
              <div className="input-container">
                <input
                  type="color"
                  name="gradient_color_1"
                  onChange={handleChange}
                  value={form?.gradient_color_1 || "#000000"}
                  className="form-control mt-2 color-picker inputs"
                  id="gradient_color_1"
                />
                {errors.gradient_color_1 && (
                  <span className="form-text text-danger">
                    Gradient 1 is required
                  </span>
                )}
              </div>
            </div>
            <div className="tiny-description-input">
              This color is applied to the home page. Goes at the top of "Top
              Fundraisers" and "How are they unique?".
            </div>
          </div>

          <div className="col-sm-6">
            <label htmlFor="gradient_color_2" className="text-gray form-labels">
              Gradient Color 2
            </label>
            <div className="row">
              <div className="input-container">
                <input
                  type="color"
                  name="gradient_color_2"
                  onChange={handleChange}
                  value={form?.gradient_color_2 || "#000000"}
                  className="form-control mt-2 color-picker inputs"
                  id="gradient_color_2"
                />
                {errors.gradient_color_2 && (
                  <span className="form-text text-danger">
                    Gradient color 2 is required
                  </span>
                )}
              </div>
            </div>
            <div className="tiny-description-input">
              This color is applied to the home page. Goes at the bottom of
              "Top Fundraisers" and "How are they unique?".
            </div>
          </div>
        </div>
      </div>

      <h4 className="p-3 form-title mt-5">General Name Settings</h4>

      <div className="col-sm-12 text-label-left">
        <div className="row">
          <div className="row">
            <div className="col-sm-12 text-gray form-labels">General Font</div>
          </div>

          <div className="col-12 col-sm-12">
            {form?.general_font ? (
              <Dropdown
                category="campaign"
                categorySubType="websiteFont"
                dropdownList={["Roboto", "Arial"]}
                dropdownHeader={form.general_font}
                handleChange={handleDropdownChange}
              />
            ) : (
              <Dropdown
                category="campaign"
                categorySubType="websiteFont"
                dropdownList={["Roboto", "Arial"]}
                dropdownHeader="Roboto"
                handleChange={handleDropdownChange}
              />
            )}
            {errors.general_font && (
              <span className="form-text text-danger">
                General font is required
              </span>
            )}
          </div>

          <div className="row">
            <div className="tiny-description-input col-sm-12">
              This font is applied to the whole website.
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="row">
            <div className="col-sm-12 text-gray form-labels">Sponsor Name</div>
          </div>

          <div className="col-12 col-sm-12">
            <input
              type="text"
              name="sponsor_name"
              readOnly
              id="sponsor_name"
              value={sponsor.sponsor_name || ""}
              className="form-control mt-2 inputs"
            />
          </div>

          <div className="row">
            <div className="tiny-description-input col-sm-12">
              This space is to fill the name of the sponsor or admin
              organization of this website.
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="row">
            <div className="col-sm-12 text-gray form-labels">
              Beneficiary Name (Singular)
            </div>
          </div>

          <div className="col-12 col-sm-12">
            <input
              type="text"
              readOnly
              name="beneficiary_name_singular"
              id="beneficiary_name_singular"
              value={beneficiary.beneficiary_name || ""}
              className="form-control mt-2 inputs"
            />
          </div>

          <div className="row">
            <div className="tiny-description-input col-sm-12">
              This space is to fill the name of the beneficiary or Non
              Govermmental Organization.
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="row">
            <div className="col-sm-12 text-gray form-labels">
              Beneficiary Name (Possessive)
            </div>
          </div>

          <div className="col-12 col-sm-12">
            <input
              type="text"
              readOnly
              name="beneficiary_possessive_possessive"
              id="beneficiary_possessive_possessive"
              value={beneficiary.beneficiary_possessive_name || ""}
              className="form-control mt-2 inputs"
            />
          </div>

          <div className="row">
            <div className="tiny-description-input col-sm-12">
              This space is to fill the name of the Beneficiary or NGO in
              possessive.
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="row">
            <div className="col-sm-12 text-gray form-labels">NFT Name</div>
          </div>

          <div className="col-12 col-sm-12">
            <input
              type="text"
              name="nft_name"
              id="nft_name"
              onChange={handleChange}
              value={form?.nft_name || ""}
              className="form-control mt-2 inputs"
              maxLength={45}
            />
            {errors.nft_name && (
              <span className="form-text text-danger">
                NFT Name is required
              </span>
            )}
          </div>

          <div className="row">
            <div className="tiny-description-input col-sm-12">
              This field is for entering the name of the NFT used on this
              website. Example: 'Pokemon'.
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="row">
            <div className="col-sm-12 text-gray form-labels">
              NFT Name (Plural)
            </div>
          </div>

          <div className="col-12 col-sm-12">
            <input
              type="text"
              name="nft_name_plural"
              id="nft_name_plural"
              onChange={handleChange}
              value={form?.nft_name_plural || ""}
              className="form-control mt-2 inputs"
              maxLength={45}
            />
            {errors.nft_name_plural && (
              <span className="form-text text-danger">
                NFT name in plural is required
              </span>
            )}
          </div>

          <div className="row">
            <div className="tiny-description-input col-sm-12">
              This field is for entering the plural form of the NFT name used on
              this website. Example: 'Pokemons'.
            </div>
          </div>
        </div>
         <div className="row mt-4">
          <div className="row">
            <div className="col-sm-12 text-gray form-labels">
              Hero Page Text
            </div>
          </div>

          <div className="col-12 col-sm-12">
            <input
              type="text"
              name="hero_page_text"
              id="hero_page_text"
              onChange={handleChange}
              value={form?.hero_page_text || "Design your own unique digital art while helping this beneficiary. We’re calling for your help!"}
              className="form-control mt-2 inputs"
              maxLength={240}
            />
            {errors.hero_page_text && (
              <span className="form-text text-danger">
                Hero Page text
              </span>
            )}
          </div>

          <div className="row">
            <div className="tiny-description-input col-sm-12">
              This is the first message your supporters will see in your campaign.
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="row">
            <div className="col-sm-12 text-gray form-labels">Purpose</div>
          </div>

          <div className="col-12 col-sm-12">
            <input
              type="text"
              name="purpose"
              onChange={handleChange}
              value={form?.purpose || ""}
              className="form-control mt-2 inputs"
              id="purpose"
              maxLength={100}
            />
            {errors.purpose && (
              <span className="form-text text-danger">
                Purpose is required
              </span>
            )}
          </div>

          <div className="row">
            <div className="tiny-description-input col-sm-12">
              Describe the purpose of this campaign or the reason for its
              creation. Example: 'Helping people discover and collect rare
              Pokemon'.
            </div>
          </div>
        </div>

        <div className="row mt-4">
          <div className="col-sm-12">
            <div className="desc-card">
              <div className="row">
                <div className="col-sm-12 text-gray form-labels">
                  Text below "Top Fundraisers"
                </div>
              </div>

              <textarea
                name="ct_about_top_fundraisers"
                id="ct_about_top_fundraisers"
                value={
                  form?.ct_about_top_fundraisers || DEFAULT_TOP_FUNDRAISERS_TEXT
                }
                onChange={handleChange}
                className="form-control mt-2 inputs"
                rows="5"
                style={{ resize: "none" }}
                maxLength={400}
              ></textarea>

              {topFundraisersLimit && (
                <span className="form-text text-danger">
                  Maximum character limit reached
                </span>
              )}
            </div>
            <div className="row">
              <div className="tiny-description-input col-sm-12">
                This text is for explaining that the NFTs displayed are the Top
                Fundraisers of this campaign.
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <button
          type="button"
          className="btn bg-orange form-btn"
          onClick={handleClick}
          disabled={hasUnsavedChanges}
        >
          Save Changes
        </button>
      </div>
    </>
  );
};

export default Form;